import { z } from 'zod';

export const PLATFORMS = ['windows', 'linux', 'android'] as const;
export const platformSchema = z.enum(PLATFORMS);
export type Platform = z.infer<typeof platformSchema>;

export const ARCHS = ['x86', 'x64', 'arm64', 'universal'] as const;
export const archSchema = z.enum(ARCHS);
export type Arch = z.infer<typeof archSchema>;

export const PACKAGE_KINDS = ['msix', 'appx', 'msi', 'exe', 'zip'] as const;
export const packageKindSchema = z.enum(PACKAGE_KINDS);
export type PackageKind = z.infer<typeof packageKindSchema>;

export const INSTALL_SCOPES = ['per_user', 'machine', 'portable'] as const;
export const installScopeSchema = z.enum(INSTALL_SCOPES);
export type InstallScope = z.infer<typeof installScopeSchema>;

export const SIGNATURE_STATUSES = ['none', 'valid', 'invalid', 'unverified'] as const;
export const signatureStatusSchema = z.enum(SIGNATURE_STATUSES);
export type SignatureStatus = z.infer<typeof signatureStatusSchema>;

export const AV_STATUSES = ['pending', 'clean', 'infected', 'error'] as const;
export const avStatusSchema = z.enum(AV_STATUSES);
export type AvStatus = z.infer<typeof avStatusSchema>;

export const RELEASE_STATUSES = ['in_review', 'published', 'rejected', 'yanked'] as const;
export const releaseStatusSchema = z.enum(RELEASE_STATUSES);
export type ReleaseStatus = z.infer<typeof releaseStatusSchema>;

export const APP_ORIGINS = ['native', 'winget'] as const;
export const appOriginSchema = z.enum(APP_ORIGINS);
export type AppOrigin = z.infer<typeof appOriginSchema>;

export const APP_STATUSES = ['draft', 'in_review', 'published', 'delisted'] as const;
export const appStatusSchema = z.enum(APP_STATUSES);
export type AppStatus = z.infer<typeof appStatusSchema>;

/**
 * Fixed category list. Management UI only reorders/translates; adding a
 * category is a code change so clients always know the full enum.
 */
export const CATEGORY_IDS = [
  'audio-video',
  'business',
  'communication',
  'development',
  'education',
  'games',
  'graphics',
  'health',
  'news',
  'productivity',
  'security',
  'system',
  'utilities',
] as const;
export const categoryIdSchema = z.enum(CATEGORY_IDS);
export type CategoryId = z.infer<typeof categoryIdSchema>;

export const LOCALES = ['en', 'ru'] as const;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;

export const MATCH_CONFIDENCES = ['exact', 'probable', 'unknown'] as const;
export const matchConfidenceSchema = z.enum(MATCH_CONFIDENCES);
export type MatchConfidence = z.infer<typeof matchConfidenceSchema>;

export function isPlatform(value: unknown): value is Platform {
  return typeof value === 'string' && (PLATFORMS as readonly string[]).includes(value);
}

export function platformsForHost(os: Platform): readonly Platform[] {
  switch (os) {
    case 'windows':
      return ['windows'];
    case 'linux':
      return ['linux'];
    case 'android':
      return ['android'];
  }
}

export function packageKindsFor(platform: Platform): readonly PackageKind[] {
  switch (platform) {
    case 'windows':
      return ['msix', 'appx', 'msi', 'exe', 'zip'];
    case 'linux':
      return ['zip'];
    case 'android':
      return [];
  }
}

/** Localized string map keyed by locale, with English as the guaranteed fallback. */
export const i18nTextSchema = z.record(localeSchema, z.string().min(1).max(4000));
export type I18nText = z.infer<typeof i18nTextSchema>;

/** I18n text as stored on a release (changelog) — English is not enforced there. */
export type PartialI18nText = Partial<Record<Locale, string>>;

export function pickLocale(text: Partial<Record<Locale, string>>, locale: Locale): string {
  return text[locale] ?? text.en ?? '';
}

/**
 * Windows OS version string `major.minor.build`, e.g. "10.0.17763".
 */
export const osVersionSchema = z.string().regex(/^\d+\.\d+\.\d+$/, 'expected major.minor.build');
export type OsVersion = z.infer<typeof osVersionSchema>;
